// Base Embla Carousel component
// Import and extend this class in a new slider component for specific carousel implementations

import EmblaCarousel from 'embla-carousel';

export default class EmblaBase extends HTMLElement {
  constructor() {
    super();
    this.dotNodes = [];
    this.options = { loop: true }; // Default options

    // Element selectors - can be overridden by subclasses
    this.rootSelector = '.embla';
    this.viewportSelector = '.embla__viewport';
    this.prevBtnSelector = '.embla__button--prev';
    this.nextBtnSelector = '.embla__button--next';
    this.dotsSelector = '.embla__dots';
  }

  connectedCallback() {
    requestAnimationFrame(() => this.initEmbla());
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this.emblaApi) {
      this.emblaApi.destroy();
      this.emblaApi = null;
    }

    // Clear any cached references
    this.dotNodes = [];
    this.emblaRoot = null;
    this.viewportNode = null;
    this.prevBtnNode = null;
    this.nextBtnNode = null;
    this.dotsNode = null;
  }

  // Find DOM elements based on selectors
  findElements() {
    this.emblaRoot = this.querySelector(this.rootSelector);
    if (!this.emblaRoot) return false;

    this.viewportNode = this.emblaRoot.querySelector(this.viewportSelector);
    this.prevBtnNode = this.emblaRoot.querySelector(this.prevBtnSelector);
    this.nextBtnNode = this.emblaRoot.querySelector(this.nextBtnSelector);
    this.dotsNode = this.emblaRoot.querySelector(this.dotsSelector);

    return this.viewportNode && this.prevBtnNode && this.nextBtnNode && this.dotsNode;
  }

  initEmbla() {
    // Find required elements
    if (!this.findElements()) {
      console.warn('Required elements not found for EmblaCarousel');
      return;
    }

    // Create carousel with options and plugins
    const plugins = this.createPlugins();
    this.emblaApi = EmblaCarousel(this.viewportNode, this.options, plugins);

    // Initialize UI controls
    this.initControls();

    // Ensure cleanup on destroy
    this.emblaApi.on('destroy', () => this.cleanup());
  }

  // Can be overridden to add or modify plugins
  createPlugins() {
    return []; // Default: no plugins
  }

  // Initialize all controls - can be overridden for custom behavior
  initControls() {
    this.initDotButtons();
    this.initPrevNextButtons();
  }

  onNavButtonClick() {
    const autoplay = this.emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    const resetOrStop = autoplay.options.stopOnInteraction === false
      ? autoplay.reset
      : autoplay.stop;

    resetOrStop();
  }

  // Handle dot navigation
  initDotButtons() {
    // Create dot buttons based on slide count
    this.dotsNode.innerHTML = this.emblaApi
      .scrollSnapList()
      .map(() => '<button class="embla__dot" type="button"></button>')
      .join('');

    // Store references to dot nodes
    this.dotNodes = Array.from(this.dotsNode.querySelectorAll('.embla__dot'));

    // Add click handlers to each dot
    this.dotNodes.forEach((dotNode, index) => {
      dotNode.addEventListener('click', () => {
        this.emblaApi.scrollTo(index);
        this.onNavButtonClick();
      });
    });

    // Set up event handlers for updating active dot state
    const toggleDotBtnsActive = () => {
      const previous = this.emblaApi.previousScrollSnap();
      const selected = this.emblaApi.selectedScrollSnap();
      this.dotNodes[previous]?.classList.remove('embla__dot--selected');
      this.dotNodes[selected]?.classList.add('embla__dot--selected');
    };

    // Attach event listeners
    this.emblaApi
      .on('init', toggleDotBtnsActive)
      .on('reInit', toggleDotBtnsActive)
      .on('select', toggleDotBtnsActive);
  }

  // Handle prev/next navigation buttons
  initPrevNextButtons() {
    // Set up button states based on carousel status
    const togglePrevNextBtnsState = () => {
      if (this.emblaApi.canScrollPrev()) this.prevBtnNode.removeAttribute('disabled');
      else this.prevBtnNode.setAttribute('disabled', 'disabled');

      if (this.emblaApi.canScrollNext()) this.nextBtnNode.removeAttribute('disabled');
      else this.nextBtnNode.setAttribute('disabled', 'disabled');
    };

    // Define scroll handlers
    const scrollPrev = () => {
      this.emblaApi.scrollPrev();
      this.onNavButtonClick();
    };

    const scrollNext = () => {
      this.emblaApi.scrollNext();
      this.onNavButtonClick();
    };

    // Add event listeners
    this.prevBtnNode.addEventListener('click', scrollPrev);
    this.nextBtnNode.addEventListener('click', scrollNext);

    // Update button states when selection changes
    this.emblaApi
      .on('init', togglePrevNextBtnsState)
      .on('reInit', togglePrevNextBtnsState)
      .on('select', togglePrevNextBtnsState);

    // Ensure we clean up event listeners when component is destroyed
    this.emblaApi.on('destroy', () => {
      this.prevBtnNode.removeEventListener('click', scrollPrev);
      this.nextBtnNode.removeEventListener('click', scrollNext);
    });
  }
}
